import { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, BODY, MONO } from '../lib/constants';
import { Btn, Mo, Bd, Dp, Ic } from './shared';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function BankManageScreen() {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linkToken, setLinkToken] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [plaidStatusRes, plaidAccRes, plaidTxnRes] = await Promise.all([
        fetch(`${API}/plaid/status`, { credentials: 'include' }),
        fetch(`${API}/plaid/accounts`, { credentials: 'include' }),
        fetch(`${API}/plaid/transactions?days=30`, { credentials: 'include' })
      ]);
      
      if (plaidStatusRes.ok) {
        const statusData = await plaidStatusRes.json();
        if (statusData.status === 'connected') {
          setStatus({ ...statusData, provider: 'plaid' });
          if (plaidAccRes.ok) {
            const accData = await plaidAccRes.json();
            setAccounts(accData.accounts || []);
          }
          if (plaidTxnRes.ok) {
            const txnData = await plaidTxnRes.json();
            setTransactions(txnData.transactions || []);
          }
          // Check connection health
          try {
            const healthRes = await fetch(`${API}/insights/accounts`, { credentials: 'include' });
            if (healthRes.ok) {
              const h = await healthRes.json();
              setNeedsReauth(!!h.needs_reauth);
            }
          } catch {}
          setLoading(false);
          return;
        }
      }
      
      setStatus({ status: 'disconnected' });
      setAccounts([]);
      setTransactions([]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        const res = await fetch(`${API}/plaid/create_link_token`, {
          method: 'POST',
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setLinkToken(data.link_token);
        }
      } catch (e) { console.error(e); }
    };
    fetchLinkToken();
    fetchData();
  }, [fetchData]);

  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setSyncing(true);
    try {
      await fetch(`${API}/plaid/exchange_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_token: publicToken, metadata }),
        credentials: 'include'
      });
      await fetchData();
    } catch (e) { console.error(e); }
    setSyncing(false);
  }, [fetchData]);

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: () => {},
  });

  const handleSync = async () => {
    setSyncing(true);
    try {
      await fetch(`${API}/plaid/sync`, { method: 'POST', credentials: 'include' });
      await fetchData();
    } catch (e) { console.error(e); }
    setSyncing(false);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const itemId = status?.connections?.[0]?.item_id;
      const res = await fetch(`${API}/plaid/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId || null }),
        credentials: 'include'
      });
      if (res.ok) {
        setStatus({ status: 'disconnected' });
        setAccounts([]);
        setTransactions([]);
        setConfirmDisconnect(false);
      }
    } catch (e) { console.error(e); }
    setDisconnecting(false);
  };

  if (loading) return (
    <div data-testid="bank-manage-loading" style={{ padding: '60px 28px', textAlign: 'center' }}>
      <div className="pulse-a">{Ic.bank(32, GOLD)}</div>
    </div>
  );

  const isConnected = status?.status === 'connected';
  const institutionName = status?.connections?.[0]?.institution_name || 'Bank';

  // Deduplicate accounts by account_id on frontend too
  const seen = new Set();
  const uniqueAccounts = accounts.filter(acc => {
    const key = acc.account_id || acc.name + acc.mask;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return (
    <div data-testid="bank-manage" style={{ padding: '20px 28px' }}>
      {/* Connection Status */}
      <div style={{ padding: 16, background: 'var(--surface)', border: `1px solid ${FAINT}`, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {Ic.bank(20, isConnected ? GOLD : GHOST)}
            <div>
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{isConnected ? institutionName : 'No Bank Connected'}</Bd>
              <Mo style={{ fontSize: 10, color: isConnected ? GOLD : EMBER, letterSpacing: 0.5 }}>
                {isConnected ? 'Connected via Plaid' : 'Disconnected'}
              </Mo>
            </div>
          </div>
          {isConnected && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: GOLD }} />
            </div>
          )}
        </div>
        {status?.connections?.[0]?.last_sync && (
          <Mo style={{ fontSize: 9, color: 'var(--ghost)', display: 'block', marginTop: 8 }}>
            Last sync: {new Date(status.connections[0].last_sync).toLocaleString()}
          </Mo>
        )}
      </div>

      {/* Re-auth Banner */}
      {needsReauth && isConnected && (
        <div data-testid="reauth-banner" style={{ padding: 16, background: `${EMBER}08`, border: `1px solid ${EMBER}25`, marginBottom: 16, borderRadius: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={EMBER} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            <div style={{ flex: 1 }}>
              <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)', display: 'block' }}>Connection needs refresh</Bd>
              <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
                Your bank session has expired. Reconnect to sync fresh data.
              </Mo>
            </div>
          </div>
          <button onClick={() => plaidReady && openPlaidLink()} disabled={!plaidReady} data-testid="reauth-reconnect-btn" style={{
            width: '100%', padding: '12px', background: EMBER, border: 'none',
            color: '#fff', fontFamily: MONO, fontSize: 11, fontWeight: 600,
            cursor: plaidReady ? 'pointer' : 'not-allowed', letterSpacing: 0.5, borderRadius: 8,
            opacity: plaidReady ? 1 : 0.5,
          }}>RECONNECT {institutionName.toUpperCase()}</button>
        </div>
      )}

      {/* Accounts */}
      {uniqueAccounts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Mo style={{ fontSize: 9, fontWeight: 700, color: 'var(--mute)', letterSpacing: 2, marginBottom: 8, display: 'block' }}>LINKED ACCOUNTS</Mo>
          {uniqueAccounts.map((acc, i) => {
            const isCredit = (acc.subtype || '').toLowerCase() === 'credit card' || (acc.type || '').toLowerCase() === 'credit';
            const isChecking = (acc.subtype || '').toLowerCase() === 'checking';
            return (
              <div key={acc.account_id || i} data-testid={`bank-account-${i}`} style={{
                padding: 14, background: 'var(--surface)', border: `1px solid ${FAINT}`, marginBottom: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)' }}>{acc.name || acc.official_name}</Bd>
                    {isChecking && (
                      <Mo style={{ fontSize: 7, fontWeight: 700, color: '#22c55e', background: 'rgba(34,197,94,0.15)', padding: '1px 5px', borderRadius: 3 }}>CHECKING</Mo>
                    )}
                    {isCredit && (
                      <Mo style={{ fontSize: 7, fontWeight: 700, color: 'var(--mute)', background: 'var(--faint)', padding: '1px 5px', borderRadius: 3 }}>CREDIT</Mo>
                    )}
                  </div>
                  <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block' }}>
                    {acc.type}{acc.subtype ? ` · ${acc.subtype}` : ''}{acc.mask ? ` · ····${acc.mask}` : ''}
                  </Mo>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Bd style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>
                    ${parseFloat(acc.current_balance || acc.available_balance || 0).toLocaleString()}
                  </Bd>
                  <Mo style={{ fontSize: 9, color: 'var(--ghost)' }}>current</Mo>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent Transactions */}
      {transactions.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Mo style={{ fontSize: 9, fontWeight: 700, color: 'var(--mute)', letterSpacing: 2, marginBottom: 8, display: 'block' }}>RECENT TRANSACTIONS</Mo>
          {transactions.slice(0, 10).map((txn, i) => (
            <div key={i} data-testid={`bank-txn-${i}`} style={{
              padding: '10px 14px', background: 'var(--surface)', border: `1px solid ${FAINT}`, marginBottom: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Bd style={{ fontSize: 12, fontWeight: 600, color: 'var(--light)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {txn.name || txn.merchant_name || 'Transaction'}
                </Bd>
                <Mo style={{ fontSize: 9, color: 'var(--ghost)' }}>{txn.date}{txn.category ? ` · ${txn.category}` : ''}</Mo>
              </div>
              <Bd style={{ fontSize: 13, fontWeight: 600, color: parseFloat(txn.amount) > 0 ? EMBER : GOLD, flexShrink: 0, marginLeft: 12 }}>
                ${Math.abs(parseFloat(txn.amount)).toFixed(2)}
              </Bd>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {isConnected ? (
          <>
            <Btn onClick={handleSync} gold disabled={syncing} data-testid="sync-bank-btn">
              {syncing ? 'SYNCING...' : 'SYNC NOW'}
            </Btn>
            <Btn onClick={() => plaidReady && openPlaidLink()} secondary disabled={!plaidReady} data-testid="add-another-bank-btn">
              + ADD ANOTHER BANK
            </Btn>
            <button
              data-testid="disconnect-bank-btn"
              onClick={() => setConfirmDisconnect(true)}
              style={{
                background: 'none', border: `1px solid ${EMBER}40`, borderRadius: 8,
                color: EMBER, fontFamily: MONO, fontSize: 11, letterSpacing: 1,
                padding: '14px 20px', cursor: 'pointer', minHeight: 48,
              }}
            >
              DISCONNECT {institutionName.toUpperCase()}
            </button>
          </>
        ) : (
          <Btn onClick={() => plaidReady && openPlaidLink()} gold disabled={!plaidReady} data-testid="reconnect-bank-btn">
            {!plaidReady ? 'LOADING...' : 'CONNECT YOUR BANK'}
          </Btn>
        )}
      </div>
      
      <Mo style={{ fontSize: 9, color: 'var(--ghost)', display: 'block', textAlign: 'center', marginTop: 16 }}>
        Powered by Plaid · 11,000+ banks · Affirm, Klarna & BNPL supported
      </Mo>

      {/* Disconnect Confirmation Modal */}
      {confirmDisconnect && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28 }}>
          <div data-testid="disconnect-confirm-modal" style={{ background: 'var(--surface)', border: `1px solid ${EMBER}40`, borderRadius: 16, padding: 24, maxWidth: 400, width: '100%' }}>
            <Mo style={{ fontSize: 10, color: EMBER, letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>DISCONNECT BANK</Mo>
            <Bd style={{ fontSize: 14, color: 'var(--light)', display: 'block', lineHeight: 1.6, marginBottom: 8 }}>
              Remove <span style={{ fontWeight: 700 }}>{institutionName}</span> from Dettio?
            </Bd>
            <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6, marginBottom: 20 }}>
              This will remove all linked accounts and transaction history from this bank. You can always reconnect later or connect a different bank.
            </Bd>
            <Btn
              data-testid="confirm-disconnect-btn"
              onClick={handleDisconnect}
              disabled={disconnecting}
              style={{ marginBottom: 8, background: EMBER, color: '#fff' }}
            >
              {disconnecting ? 'DISCONNECTING...' : 'YES, DISCONNECT'}
            </Btn>
            <Btn onClick={() => setConfirmDisconnect(false)} secondary data-testid="cancel-disconnect-btn">
              CANCEL
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}
